import * as React from 'react';
import { Clock3, Gauge, Layers3, MessageSquare, RotateCcw, ShieldCheck, Users } from 'lucide-react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  type SortDirection,
} from '@/components/ui';
import { BarList, type BarListRow, LineChart, type ChartSeries } from '@/components/reports/report-charts';
import { useI18n } from '@/lib/i18n';
import type {
  AgentPerformanceReport,
  AgentPerformanceRow,
  BreakdownRow,
  CsatReport,
  RcaReport,
  SlaBreakdownRow,
  SlaReport,
  VolumeReport,
} from '@/lib/reports-api';

const COLORS = {
  blue: '#00368F',
  sky: '#3E82D9',
  red: '#DE0039',
  amber: '#C47700',
  green: '#16845B',
  violet: '#7656B8',
};

type Translator = ReturnType<typeof useI18n>['t'];
type OptionalTranslator = ReturnType<typeof useI18n>['tOr'];

function readableLabel(value: string | null | undefined, t: Translator): string {
  if (!value) return t('dir.reports.unclassified');
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatNumber(value: number | undefined | null, language: string): string {
  return new Intl.NumberFormat(language).format(value ?? 0);
}

function percent(value: number | undefined | null): string {
  return `${Math.round(value ?? 0)}%`;
}

function formatSeconds(seconds: number | undefined | null): string {
  if (!seconds || seconds < 0 || !Number.isFinite(seconds)) return '—';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return '<1m';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const remainingMinutes = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(remainingMinutes).padStart(2, '0')}m`;
  return `${remainingMinutes}m`;
}

function dateAxis(from: string, to: string): string[] {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
  const dates: string[] = [];
  for (let cursor = start; cursor <= end; cursor += 86400000) dates.push(new Date(cursor).toISOString().slice(0, 10));
  return dates;
}

function dateLabel(value: string, language: string): string {
  return new Intl.DateTimeFormat(language, { day: '2-digit', month: 'short', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
}

function sum(values: Record<string, number>): number {
  return Object.values(values).reduce((total, value) => total + (Number(value) || 0), 0);
}

function recordRows(values: Record<string, number>, t: Translator, tOr: OptionalTranslator, kind?: 'status' | 'priority'): BarListRow[] {
  return Object.entries(values)
    .map(([key, value]) => ({ key, label: kind ? tOr(`${kind}.${key}`, readableLabel(key, t)) : readableLabel(key, t), value: Number(value) || 0 }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

function breakdownRows(values: BreakdownRow[], t: Translator): BarListRow[] {
  return values
    .map((row, index) => ({ key: row.key || `${row.label || 'row'}-${index}`, label: row.label || t('dir.reports.unclassified'), value: Number(row.count) || 0 }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

function MetricCard({ label, value, detail, icon: Icon, tone = 'blue' }: { label: string; value: React.ReactNode; detail?: string; icon: React.ComponentType<{ className?: string }>; tone?: 'blue' | 'red' | 'green' | 'amber' }) {
  const toneClass = { blue: 'bg-brand-50 text-brand-700', red: 'bg-accent-50 text-accent-700', green: 'bg-success-50 text-success-700', amber: 'bg-warning-50 text-warning-700' }[tone];
  return (
    <Card className="overflow-hidden">
      <CardContent className="relative p-5">
        <div className={`mb-4 flex h-9 w-9 items-center justify-center rounded-lg ${toneClass}`}><Icon className="h-4 w-4" aria-hidden="true" /></div>
        <p className="text-overline uppercase text-ink-subtle">{label}</p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-ink">{value}</p>
        {detail && <p className="mt-1 text-xs text-ink-subtle">{detail}</p>}
        <span className="absolute -right-8 -top-8 h-24 w-24 rounded-full border-[10px] border-current opacity-[0.06]" aria-hidden="true" />
      </CardContent>
    </Card>
  );
}

function ChartPanel({ eyebrow, title, description, children }: { eyebrow: string; title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b border-line pb-4">
        <p className="text-overline uppercase tracking-[0.16em] text-brand-600">{eyebrow}</p>
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <p className="text-sm font-normal text-ink-muted">{description}</p>}
      </CardHeader>
      <CardContent className="pt-5">{children}</CardContent>
    </Card>
  );
}

function BreakdownCard({ title, description, rows, color = COLORS.blue }: { title: string; description: string; rows: BarListRow[]; color?: string }) {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader className="pb-4"><CardTitle className="text-base">{title}</CardTitle><p className="text-sm font-normal text-ink-muted">{description}</p></CardHeader>
      <CardContent><BarList rows={rows} color={color} emptyLabel={t('dir.reports.noRecords')} /></CardContent>
    </Card>
  );
}

export function VolumeReportView({ data }: { data: VolumeReport }) {
  const { t, tOr, language } = useI18n();
  const number = (value: number | undefined | null) => formatNumber(value, language);
  const labels = dateAxis(data.from, data.to);
  const mapSeries = (values: Record<string, number>) => labels.map((label) => values[label] ?? 0);
  const lifecycle: ChartSeries[] = [
    { key: 'created', label: t('dir.reports.created'), values: mapSeries(data.lifecycleByDate.created), color: COLORS.blue },
    { key: 'resolved', label: t('dir.reports.resolved'), values: mapSeries(data.lifecycleByDate.resolved), color: COLORS.green },
    { key: 'reopened', label: t('dir.reports.reopened'), values: mapSeries(data.lifecycleByDate.reopened), color: COLORS.red },
  ];
  const created = sum(data.lifecycleByDate.created);
  const resolved = sum(data.lifecycleByDate.resolved);
  const reopened = sum(data.lifecycleByDate.reopened);
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('dir.reports.ticketsInPeriod')} value={number(data.total)} detail={t('dir.reports.ticketsInPeriodDetail')} icon={Layers3} />
        <MetricCard label={t('dir.reports.created')} value={number(created)} detail={t('dir.reports.createdDetail')} icon={MessageSquare} />
        <MetricCard label={t('dir.reports.resolved')} value={number(resolved)} detail={created ? t('dir.reports.resolvedDetail', { percent: Math.round((resolved / created) * 100) }) : t('dir.reports.noCreated')} icon={ShieldCheck} tone="green" />
        <MetricCard label={t('dir.reports.reopened')} value={number(reopened)} detail={t('dir.reports.reopenedDetail')} icon={RotateCcw} tone={reopened ? 'red' : 'blue'} />
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <ChartPanel eyebrow={t('dir.reports.dailyPulse')} title={t('dir.reports.volumeByDate')} description={t('dir.reports.volumeByDateDescription')}>
          <LineChart labels={labels.map((label) => dateLabel(label, language))} series={[{ key: 'created', label: t('dir.reports.createdTickets'), values: mapSeries(data.byDate), color: COLORS.blue }]} ariaLabel={t('dir.reports.createdVolumeAria')} />
        </ChartPanel>
        <ChartPanel eyebrow={t('dir.reports.lifecycle')} title={t('dir.reports.lifecycleTitle')} description={t('dir.reports.lifecycleDescription')}>
          <LineChart labels={labels.map((label) => dateLabel(label, language))} series={lifecycle} ariaLabel={t('dir.reports.lifecycleAria')} />
        </ChartPanel>
      </div>
      <div>
        <div className="mb-3 flex items-end justify-between gap-3"><div><p className="text-overline uppercase tracking-[0.16em] text-brand-600">{t('dir.reports.dimensions')}</p><h2 className="mt-1 text-lg font-semibold text-ink">{t('dir.reports.whereConcentrated')}</h2></div><p className="text-xs text-ink-subtle">{t('dir.reports.topEight')}</p></div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <BreakdownCard title={t('dir.reports.status')} description={t('dir.reports.statusDescription')} rows={recordRows(data.byStatus, t, tOr, 'status')} color={COLORS.blue} />
          <BreakdownCard title={t('dir.reports.priority')} description={t('dir.reports.priorityDescription')} rows={recordRows(data.byPriority, t, tOr, 'priority')} color={COLORS.red} />
          <BreakdownCard title={t('dir.reports.group')} description={t('dir.reports.groupDescription')} rows={recordRows(data.byGroup, t, tOr)} color={COLORS.sky} />
          <BreakdownCard title={t('dir.reports.agent')} description={t('dir.reports.agentDescription')} rows={breakdownRows(data.byAgent, t)} color={COLORS.violet} />
          <BreakdownCard title={t('dir.reports.product')} description={t('dir.reports.productDescription')} rows={breakdownRows(data.byProduct, t)} color={COLORS.green} />
          <BreakdownCard title={t('dir.reports.country')} description={t('dir.reports.countryDescription')} rows={recordRows(data.byCountry, t, tOr)} color={COLORS.amber} />
        </div>
      </div>
    </div>
  );
}

function summaryText(summary: { rate: number; met: number; total: number }, t: Translator, number: (value: number) => string): string {
  return t('dir.reports.slaSummary', { rate: percent(summary.rate), met: number(summary.met), total: number(summary.total) });
}

function SlaBreakdownTable({ rows }: { rows: SlaBreakdownRow[] }) {
  const { t, language } = useI18n();
  const number = (value: number | undefined | null) => formatNumber(value, language);
  if (rows.length === 0) return <EmptyState title={t('dir.reports.noSlaSegments')} description={t('dir.reports.noSlaSegmentsDescription')} />;
  return (
    <Table>
      <TableHeader><TableRow><TableHead>{t('dir.reports.segment')}</TableHead><TableHead>{t('dir.reports.firstResponse')}</TableHead><TableHead>{t('dir.reports.resolution')}</TableHead><TableHead>{t('dir.reports.avgFirstResponse')}</TableHead><TableHead>{t('dir.reports.avgResolution')}</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((row, index) => <TableRow key={row.key || `${row.label || 'segment'}-${index}`}>
        <TableCell><p className="font-medium">{row.label || t('dir.reports.unclassified')}</p>{row.key && row.key !== row.label && <p className="text-xs text-ink-subtle">{row.key}</p>}</TableCell>
        <TableCell><span className="font-medium text-ink">{percent(row.firstResponse.rate)}</span><span className="ml-2 text-xs text-ink-subtle">{number(row.firstResponse.met)}/{number(row.firstResponse.total)}</span></TableCell>
        <TableCell><span className="font-medium text-ink">{percent(row.resolution.rate)}</span><span className="ml-2 text-xs text-ink-subtle">{number(row.resolution.met)}/{number(row.resolution.total)}</span></TableCell>
        <TableCell className="font-mono text-sm">{formatSeconds(row.averageFirstResponseSeconds)}</TableCell>
        <TableCell className="font-mono text-sm">{formatSeconds(row.averageResolutionSeconds)}</TableCell>
      </TableRow>)}</TableBody>
    </Table>
  );
}

export function SlaReportView({ data }: { data: SlaReport }) {
  const { t, language } = useI18n();
  const number = (value: number) => formatNumber(value, language);
  const [dimension, setDimension] = React.useState<'priority' | 'group' | 'agent'>('priority');
  const rows = dimension === 'priority' ? data.byPriority : dimension === 'group' ? data.byGroup : data.byAgent;
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('dir.reports.firstResponse')} value={percent(data.firstResponse.rate)} detail={summaryText(data.firstResponse, t, number)} icon={Clock3} tone={data.firstResponse.rate >= 90 ? 'green' : 'amber'} />
        <MetricCard label={t('dir.reports.resolution')} value={percent(data.resolution.rate)} detail={summaryText(data.resolution, t, number)} icon={ShieldCheck} tone={data.resolution.rate >= 90 ? 'green' : 'amber'} />
        <MetricCard label={t('dir.reports.avgFirstResponse')} value={formatSeconds(data.averageFirstResponseSeconds)} detail={t('dir.reports.fromTicketMetric')} icon={Gauge} />
        <MetricCard label={t('dir.reports.avgResolution')} value={formatSeconds(data.averageResolutionSeconds)} detail={t('dir.reports.fromTicketMetric')} icon={Gauge} tone="green" />
      </div>
      <Card>
        <CardHeader className="border-b border-line pb-0"><p className="text-overline uppercase tracking-[0.16em] text-brand-600">{t('dir.reports.slaLens')}</p><CardTitle className="mt-1 text-base">{t('dir.reports.performanceBySegment')}</CardTitle><p className="pb-4 text-sm font-normal text-ink-muted">{t('dir.reports.slaRatesNote')}</p><Tabs aria-label={t('dir.reports.slaDimensionAria')} value={dimension} onChange={(value) => setDimension(value as typeof dimension)} items={[{ value: 'priority', label: t('dir.reports.priority'), count: data.byPriority.length }, { value: 'group', label: t('dir.reports.group'), count: data.byGroup.length }, { value: 'agent', label: t('dir.reports.agent'), count: data.byAgent.length }]} /></CardHeader>
        <CardContent className="p-0"><SlaBreakdownTable rows={rows} /></CardContent>
      </Card>
    </div>
  );
}

type AgentSortKey = 'name' | 'assigned' | 'resolved' | 'replies' | 'averageFirstResponseSeconds' | 'averageResolutionSeconds';

function agentSortLabel(key: AgentSortKey, t: Translator): string {
  const labels: Record<AgentSortKey, Parameters<Translator>[0]> = {
    name: 'dir.reports.agent',
    assigned: 'dir.reports.assigned',
    resolved: 'dir.reports.resolved',
    replies: 'dir.reports.replies',
    averageFirstResponseSeconds: 'dir.reports.avgFirstResponse',
    averageResolutionSeconds: 'dir.reports.avgResolution',
  };
  return t(labels[key]);
}

function compareAgents(a: AgentPerformanceRow, b: AgentPerformanceRow, key: AgentSortKey): number {
  if (key === 'name') return a.name.localeCompare(b.name);
  return (Number(a[key]) || 0) - (Number(b[key]) || 0);
}

export function AgentPerformanceReportView({ data }: { data: AgentPerformanceReport }) {
  const { t, language } = useI18n();
  const number = (value: number | undefined | null) => formatNumber(value, language);
  const [search, setSearch] = React.useState('');
  const [sortKey, setSortKey] = React.useState<AgentSortKey>('resolved');
  const [direction, setDirection] = React.useState<SortDirection>('desc');
  const rows = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...data.data]
      .filter((agent) => !query || `${agent.name} ${agent.email}`.toLowerCase().includes(query))
      .sort((a, b) => compareAgents(a, b, sortKey) * (direction === 'asc' ? 1 : -1));
  }, [data.data, direction, search, sortKey]);

  function sortBy(key: string) {
    const next = key as AgentSortKey;
    if (next === sortKey) setDirection((current) => current === 'asc' ? 'desc' : 'asc');
    else { setSortKey(next); setDirection(next === 'name' ? 'asc' : 'desc'); }
  }

  return (
    <Card>
      <CardHeader className="border-b border-line pb-4"><div className="flex flex-wrap items-end justify-between gap-3"><div><p className="text-overline uppercase tracking-[0.16em] text-brand-600">{t('dir.reports.rosterSignal')}</p><CardTitle className="mt-1 text-base">{t('dir.reports.agentPerformance')}</CardTitle><p className="mt-1 text-sm font-normal text-ink-muted">{t('dir.reports.agentPerformanceDescription')}</p></div><div className="w-full sm:w-72"><Input aria-label={t('dir.reports.filterAgentsAria')} placeholder={t('dir.reports.filterAgentsPlaceholder')} value={search} onChange={(event) => setSearch(event.target.value)} /></div></div></CardHeader>
      <CardContent className="p-0"><div className="border-b border-line px-6 py-3 text-xs text-ink-subtle">{search ? t('dir.reports.matchingAgents', { count: number(rows.length) }) : t('dir.reports.activeAgents', { count: number(data.data.length) })} · {t('dir.reports.sortedBy', { key: agentSortLabel(sortKey, t), direction: direction === 'asc' ? t('dir.reports.asc') : t('dir.reports.desc') })}</div>
        {rows.length === 0 ? <EmptyState title={t('dir.reports.noAgentsMatch')} description={t('agents.tryDifferent')} /> : <Table><TableHeader><TableRow>
          <SortableTableHead sortKey="name" activeKey={sortKey} direction={direction} onSort={sortBy}>{t('dir.reports.agent')}</SortableTableHead>
          <SortableTableHead sortKey="assigned" activeKey={sortKey} direction={direction} onSort={sortBy}>{t('dir.reports.assigned')}</SortableTableHead>
          <SortableTableHead sortKey="resolved" activeKey={sortKey} direction={direction} onSort={sortBy}>{t('dir.reports.resolved')}</SortableTableHead>
          <SortableTableHead sortKey="replies" activeKey={sortKey} direction={direction} onSort={sortBy}>{t('dir.reports.replies')}</SortableTableHead>
          <SortableTableHead sortKey="averageFirstResponseSeconds" activeKey={sortKey} direction={direction} onSort={sortBy}>{t('dir.reports.avgFirstResponse')}</SortableTableHead>
          <SortableTableHead sortKey="averageResolutionSeconds" activeKey={sortKey} direction={direction} onSort={sortBy}>{t('dir.reports.avgResolution')}</SortableTableHead>
        </TableRow></TableHeader><TableBody>{rows.map((agent) => <TableRow key={agent.id}><TableCell><p className="font-medium">{agent.name}</p><p className="text-xs text-ink-subtle">{agent.email}</p></TableCell><TableCell>{number(agent.assigned)}</TableCell><TableCell>{number(agent.resolved)}</TableCell><TableCell>{number(agent.replies)}</TableCell><TableCell className="font-mono text-sm">{formatSeconds(agent.averageFirstResponseSeconds)}</TableCell><TableCell className="font-mono text-sm">{formatSeconds(agent.averageResolutionSeconds)}</TableCell></TableRow>)}</TableBody></Table>}
      </CardContent>
    </Card>
  );
}

export function CsatReportView({ data }: { data: CsatReport }) {
  const { t, language } = useI18n();
  const number = (value: number | undefined | null) => formatNumber(value, language);
  const labels = dateAxis(data.from || '', data.to || '');
  const chartLabels = labels.length ? labels : Object.keys(data.byDate).sort();
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"><MetricCard label={t('dir.reports.csatScore')} value={percent(data.score)} detail={t('dir.reports.csatScoreDetail')} icon={ShieldCheck} tone={data.score >= 90 ? 'green' : 'amber'} /><MetricCard label={t('dir.reports.averageRating')} value={data.total ? `${Number(data.averageRating).toFixed(2)} / 5` : '—'} detail={t('dir.reports.averageRatingDetail')} icon={Gauge} /><MetricCard label={t('dir.reports.ratedSurveys')} value={number(data.total)} detail={t('dir.reports.ratedSurveysDetail')} icon={MessageSquare} /><MetricCard label={t('dir.reports.satisfied')} value={number(data.satisfied)} detail={t('dir.reports.satisfiedDetail')} icon={Users} tone="green" /></div>
      <div className="grid gap-5 xl:grid-cols-2"><ChartPanel eyebrow={t('dir.reports.responseTrend')} title={t('dir.reports.surveyVolume')} description={t('dir.reports.surveyVolumeDescription')}><LineChart labels={chartLabels.map((label) => dateLabel(label, language))} series={[{ key: 'responses', label: t('dir.reports.surveyResponses'), values: chartLabels.map((label) => data.byDate[label] ?? 0), color: COLORS.blue }]} ariaLabel={t('dir.reports.surveyVolumeAria')} /></ChartPanel><div className="grid gap-5"><BreakdownCard title={t('dir.reports.responsesByGroup')} description={t('dir.reports.responsesByGroupDescription')} rows={breakdownRows(data.byGroup, t)} color={COLORS.violet} /><BreakdownCard title={t('dir.reports.responsesByAgent')} description={t('dir.reports.responsesByAgentDescription')} rows={breakdownRows(data.byAgent, t)} color={COLORS.sky} /></div></div>
      <Card><CardHeader className="border-b border-line pb-4"><div className="flex flex-wrap items-baseline justify-between gap-3"><div><p className="text-overline uppercase tracking-[0.16em] text-brand-600">{t('dir.reports.customerVoice')}</p><CardTitle className="mt-1 text-base">{t('dir.reports.latestSurveys')}</CardTitle></div><Badge variant="neutral">{t('dir.reports.boundedSample', { count: number(data.surveys.length) })}</Badge></div><p className="mt-1 text-sm text-ink-muted">{t('dir.reports.latestSurveysNote')}</p></CardHeader><CardContent className="p-0">{data.surveys.length === 0 ? <EmptyState title={t('dir.reports.noSurveys')} description={t('dir.reports.noSurveysDescription')} /> : <Table><TableHeader><TableRow><TableHead>{t('dir.reports.rating')}</TableHead><TableHead>{t('dir.ticket')}</TableHead><TableHead>{t('dir.subject')}</TableHead><TableHead>{t('dir.reports.comment')}</TableHead><TableHead>{t('dir.reports.received')}</TableHead></TableRow></TableHeader><TableBody>{data.surveys.map((survey) => <TableRow key={survey.id}><TableCell><span className="font-semibold text-ink">{'★'.repeat(Math.max(0, Math.min(5, survey.rating)))}</span><span className="sr-only">{t('dir.reports.ratingOutOf', { rating: survey.rating })}</span></TableCell><TableCell className="font-mono text-sm">#{survey.ticketNumber}</TableCell><TableCell className="max-w-[18rem] truncate" title={survey.subject}>{survey.subject}</TableCell><TableCell className="max-w-[24rem] text-sm text-ink-muted" title={survey.comment || undefined}>{survey.comment || t('dir.reports.noComment')}</TableCell><TableCell className="whitespace-nowrap text-xs text-ink-subtle">{new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(survey.createdAt))}</TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card>
    </div>
  );
}

export function RcaReportView({ data }: { data: RcaReport }) {
  const { t, tOr } = useI18n();
  const [dimension, setDimension] = React.useState<'category' | 'subcategory'>('category');
  const values = dimension === 'category' ? data.byCategory : data.bySubcategory;
  return <Card><CardHeader className="border-b border-line pb-0"><p className="text-overline uppercase tracking-[0.16em] text-brand-600">{t('dir.reports.rcaEyebrow')}</p><CardTitle className="mt-1 text-base">{t('dir.reports.rcaTitle')}</CardTitle><p className="pb-4 text-sm font-normal text-ink-muted">{t('dir.reports.rcaDescription')}</p><Tabs aria-label={t('dir.reports.rcaDimensionAria')} value={dimension} onChange={(value) => setDimension(value as typeof dimension)} items={[{ value: 'category', label: t('dir.reports.category'), count: Object.keys(data.byCategory).length }, { value: 'subcategory', label: t('dir.reports.subcategory'), count: Object.keys(data.bySubcategory).length }]} /></CardHeader><CardContent className="pt-5"><BarList rows={recordRows(values, t, tOr)} color={dimension === 'category' ? COLORS.red : COLORS.amber} emptyLabel={t('dir.reports.rcaEmpty')} /></CardContent></Card>;
}

export type ReportViewProps =
  | { kind: 'volume'; data: VolumeReport }
  | { kind: 'sla'; data: SlaReport }
  | { kind: 'agents'; data: AgentPerformanceReport }
  | { kind: 'csat'; data: CsatReport }
  | { kind: 'rca'; data: RcaReport };

export function ReportView(props: ReportViewProps) {
  if (props.kind === 'volume') return <VolumeReportView data={props.data} />;
  if (props.kind === 'sla') return <SlaReportView data={props.data} />;
  if (props.kind === 'agents') return <AgentPerformanceReportView data={props.data} />;
  if (props.kind === 'csat') return <CsatReportView data={props.data} />;
  return <RcaReportView data={props.data} />;
}
